import { ArrowLeft, ArrowRight, Award, X, Check, XCircle } from 'lucide-react';
import { useState, useEffect } from 'react';

import { useGetCertificationQuestionsQuery } from '@/@generated/codegen/API';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useBestPassedCertification } from '@/hooks/queries/useCertificationProgress';
import { useToast } from '@/hooks/use-toast';
import { useTranslations } from '@/hooks/use-translations';
import { cn } from '@/lib/utils';
import { captureSentry } from '@/utils/sentryHelper';

interface CertificationReviewProps {
  caseId: string;
  /** Masterclass ID for fetching user's attempt */
  masterclassId?: string;
  caseTitle: string;
  userId: string;
  onClose: () => void;
}

export const CertificationReview = ({
  caseId,
  masterclassId,
  caseTitle,
  userId,
  onClose,
}: CertificationReviewProps) => {
  const { t } = useTranslations();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<number[]>([]);
  const { toast } = useToast();

  // Fetch certification questions from backend
  const {
    data: certificationData,
    loading: certificationLoading,
    error: certificationError,
  } = useGetCertificationQuestionsQuery({
    variables: {
      input: { patientCaseId: caseId },
    },
    skip: !caseId,
  });

  // Apollo hook for fetching best passed attempt
  const { data: bestAttempt, isLoading: attemptLoading } = useBestPassedCertification(
    userId,
    caseId,
    masterclassId || null
  );

  const questions = certificationData?.getCertificationQuestions?.certification_questions ?? [];
  const loading = certificationLoading || attemptLoading;

  // Set user answers when best attempt is loaded
  useEffect(() => {
    if (bestAttempt) {
      const savedAnswers = Array.isArray(bestAttempt.answers)
        ? bestAttempt.answers.map((a: number | null) => (typeof a === 'number' ? a : -1))
        : [];
      setUserAnswers(savedAnswers);
    }
  }, [bestAttempt]);

  // Show error if certification failed to load
  useEffect(() => {
    if (certificationError) {
      console.error('Error loading certification review:', certificationError);
      captureSentry({
        title: 'Failed to load certification review',
        error: certificationError,
        detail: { caseId, userId },
      });
      toast({
        title: t('validation.errors.loadingCertification'),
        description: 'Unable to load certification review',
        variant: 'destructive',
      });
      onClose();
    }
  }, [certificationError, caseId, userId, t, toast, onClose]);

  // Close if no passed attempt found
  useEffect(() => {
    if (!attemptLoading && !bestAttempt && !loading) {
      toast({
        title: t('validation.errors.loadingCertification'),
        description: 'No passed certification attempt found',
        variant: 'destructive',
      });
      onClose();
    }
  }, [attemptLoading, bestAttempt, loading, t, toast, onClose]);

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Card className="p-8">
          <p className="font-body text-lg">{t('certification.test.loadingTest')}</p>
        </Card>
      </div>
    );
  }

  if (certificationError || questions.length === 0 || !bestAttempt) {
    return null;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="max-h-[90vh] w-full max-w-3xl overflow-y-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-heading text-3xl text-livelinx-purple200">
            <Award className="mr-2 inline h-8 w-8" />
            {t('certification.review.title', { caseTitle })}
          </h2>
          <Button variant="ghost" onClick={onClose} size="icon">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="mb-6">
          <div className="mb-2 flex justify-between text-sm text-livelinx-grey200">
            <span>
              {t('certification.test.questionProgress', {
                current: currentQuestionIndex + 1,
                total: questions.length,
              })}
            </span>
            <span>
              {t('certification.test.progressComplete', {
                percent: progress.toFixed(0),
              })}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="mb-8">
          <h3 className="mb-4 font-body text-xl text-livelinx-text">
            {currentQuestion?.question_text}
          </h3>

          <div className="space-y-3">
            {currentQuestion?.options.map((option, index) => {
              const isCorrect = index === currentQuestion.correct_answer;
              const isUserAnswer = index === userAnswers[currentQuestionIndex];
              const isWrong = isUserAnswer && !isCorrect;

              return (
                <div
                  key={index}
                  className={cn(
                    'mb-3 flex items-center space-x-3 rounded-lg border-2 p-4 transition-all',
                    isCorrect && 'border-livelinx-green bg-livelinx-green50',
                    isWrong && 'border-livelinx-red bg-livelinx-red/10',
                    !isCorrect && !isWrong && 'border-livelinx-lightgray100 bg-white'
                  )}
                >
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                    {isCorrect && <Check className="h-5 w-5 text-livelinx-green" />}
                    {isWrong && <XCircle className="h-5 w-5 text-livelinx-red" />}
                  </div>
                  <Label
                    className={cn(
                      'flex-1 cursor-default font-body text-base',
                      isCorrect && 'text-livelinx-green200',
                      isWrong && 'text-livelinx-red'
                    )}
                  >
                    {option}
                  </Label>
                </div>
              );
            })}
          </div>

          {currentQuestion?.explanation && (
            <div className="mt-6 rounded-lg border border-livelinx-blue300 bg-livelinx-blue50 p-4">
              <p className="font-body text-sm text-livelinx-text">
                <strong className="text-livelinx-blue300">
                  {t('certification.review.explanation')}:
                </strong>{' '}
                {currentQuestion.explanation}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-livelinx-lightgray100 pt-4">
          <Button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            variant="outline"
            className="border-livelinx-purple200 text-livelinx-purple200"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.buttons.previous')}
          </Button>

          {currentQuestionIndex === questions.length - 1 ? (
            <Button
              onClick={onClose}
              className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
            >
              {t('common.buttons.close')}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
            >
              {t('common.buttons.next')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};
